import {makeAutoObservable} from "mobx"
import {getMyStatus} from "../../api/users"
import {getBGColor} from "../../constants"
import Store from "../../stores"
import LoyaltyStoreX from "../../stores/loyalty"
import {isNullOrUndefined} from "../../utils"
import {defaultStatuses, start} from "./consts"
import {StatusCatalog} from "./StatusCatalog"
import {StatusLikeCode, getStatusCodeFromModel, resolveStatusCode, StatusItem} from "./status.types"

export default class StatusStore {
    mainStore: Store
    loyaltyStore: LoyaltyStoreX

    states: StatusItem[] = defaultStatuses
    selectedCard: StatusItem | null = defaultStatuses[0]

    isStatusRulesVisible = false
    isGiftsLoading = false
    isGiftsVisible = false
    isGiftsAVisible = false
    isStageVisible = false
    isStageStatusVisible = true
    activeStage: "main" | "rules" = "main"

    selStatus = 0
    selectedStatus: any | null = null
    selectedPrize: any | null = null
    timeToNextStatus = ""
    rulesVisible = false

    constructor(main: Store, loyalty: LoyaltyStoreX) {
        makeAutoObservable(this)
        this.mainStore = main
        this.loyaltyStore = loyalty
    }

    public get catalog() {
        // В слайдере start не показывается, но нужен в каталоге для корректных сравнений.
        const catalogStatuses = this.states.some((status) => status.code === start.code)
            ? this.states
            : [start, ...this.states]

        return new StatusCatalog(catalogStatuses)
    }

    private getStatusCode() {
        return getStatusCodeFromModel(this.loyaltyStore.myStatus)
    }

    private getSelectedStatusCode() {
        return getStatusCodeFromModel(this.loyaltyStore.selectedStatus)
    }

    private getVisibleStatusIndexByCode(code: StatusLikeCode) {
        const resolvedCode = resolveStatusCode(code)
        const statusIndex = this.states.findIndex((status) => status.code === resolvedCode)

        // Если статус не видимый (например start), открываем первый видимый статус.
        return statusIndex < 0 ? 0 : statusIndex
    }

    setRulesVisible = (val: boolean) => (this.rulesVisible = val)
    setStates = (val: StatusItem[]) => (this.states = val)
    setSelectedCard = (val: StatusItem | null) => (this.selectedCard = val)
    setIsStatusRulesVisible = (val: boolean) => (this.isStatusRulesVisible = val)
    setIsGiftsLoading = (val: boolean) => (this.isGiftsLoading = val)
    setIsGiftsVisible = (val: boolean) => (this.isGiftsVisible = val)
    setIsGiftsAVisible = (val: boolean) => (this.isGiftsAVisible = val)
    setIsStageVisible = (val: boolean) => (this.isStageVisible = val)
    setIsStageStatusVisible = (val: boolean) => (this.isStageStatusVisible = val)
    setSelStatus = (val: number) => (this.selStatus = val)
    setToNextStatus = (val: string) => (this.timeToNextStatus = val)
    setActiveStage = (val: "main" | "rules") => (this.activeStage = val)

    private async loadStatuses(): Promise<StatusItem[]> {
        return defaultStatuses
    }

    public hasFullSigVisit() {
        return this.loyaltyStore.myStatus.has_visit
    }

    public isSignatureCompleted() {
        return this.hasFullSigVisit() && !isNullOrUndefined(this.mainStore.subscribeData) && this.mainStore.subscribeData.sub_name !== "sub2"
    }

    public hasFullVisit() {
        if (this.loyaltyStore.myStatus.has_visit) {
            return true
        }

        return this.loyaltyStore.statusHasVisit
    }

    public isActualCompleted() {
        const currentCode = this.getStatusCode()
        const selectedCard = this.selectedCard

        if (this.catalog.isLast(currentCode) || selectedCard === null) {
            return true
        }

        if (!this.hasFullVisit()) {
            return false
        }

        const selectedPrev = this.catalog.previousOf(selectedCard)
        // Условия считаются выполненными только для выбранного статуса или ближайшего следующего.
        if (selectedPrev && this.catalog.isBefore(currentCode, selectedPrev.code)) {
            return false
        }

        if (this.loyaltyStore.myStatus.need_plus) {
            if (!isNullOrUndefined(this.mainStore.subscribeData) && this.mainStore.subscribeData.sub_name !== "sub2") {
                return false
            }
        }

        return true
    }

    private setStageByCode(code: StatusLikeCode, statuses: StatusItem[]) {
        const activeIndex = this.getVisibleStatusIndexByCode(code)
        this.setSelectedCard(statuses[activeIndex] ?? statuses[0] ?? null)
        this.loyaltyStore.setGradStatus(getBGColor(activeIndex))
        if (this.loyaltyStore.swiper !== null) {
            this.loyaltyStore.swiper.slideTo(activeIndex)
        }
    }

    private openStageAfterLoad() {
        setTimeout(() => {
            if (this.isSignatureCompleted()) {
                this.loyaltyStore.setGradStatus(getBGColor(6))
                if (this.loyaltyStore.swiper !== null) {
                    this.loyaltyStore.swiper.slideTo(6)
                }
            }

            this.setIsStageVisible(true)
        }, 300)
    }

    public async fetchFixData() {
        const statuses = await this.loadStatuses()
        this.setStates(statuses)

        if (this.loyaltyStore.selectedStatus !== null) {
            this.setStageByCode(this.getSelectedStatusCode(), statuses)
        } else {
            this.setStageByCode(this.getStatusCode(), statuses)
        }

        this.openStageAfterLoad()
    }

    private mapApiStatusToModel(apiStatus: any) {
        const {name, ...rest} = apiStatus
        const code = resolveStatusCode(rest.code ?? name)

        // API еще может присылать legacy name, поэтому временно дублируем code в name.
        return {
            ...rest,
            code,
            name: code
        }
    }

    public async fetchData() {
        const res = await getMyStatus()

        if (!isNullOrUndefined(res.status) && res.status.id !== 0) {
            const newStatus = this.mapApiStatusToModel(res.status)
            this.loyaltyStore.setMyStatus(newStatus)

            const statuses = await this.loadStatuses()
            this.setStates(statuses)

            if (this.loyaltyStore.selectedStatus !== null) {
                this.setStageByCode(this.getSelectedStatusCode(), statuses)
            } else {
                this.setStageByCode(newStatus.code, statuses)
            }

            this.openStageAfterLoad()
        } else {
            this.loyaltyStore.closeStatuses()
        }
    }

}
